import { useQuery, useMutation } from '@tanstack/react-query'

// These endpoints live on the v1 Express server (/api), not Laravel (/api/v2)
const V1 = '/api'

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function v1get<T>(path: string): Promise<T> {
  const res = await fetch(`${V1}${path}`, { headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v1post<T>(path: string, data?: unknown): Promise<T> {
  const res = await fetch(`${V1}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: data ? JSON.stringify(data) : undefined,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v1put<T>(path: string, data?: unknown): Promise<T> {
  const res = await fetch(`${V1}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: data ? JSON.stringify(data) : undefined,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function v1del<T>(path: string): Promise<T> {
  const res = await fetch(`${V1}${path}`, { method: 'DELETE', headers: authHeaders() })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export interface PagePost {
  id: string
  message?: string
  created_time: string
  full_picture?: string
  likes: { summary: { total_count: number } }
  comments: { summary: { total_count: number } }
  shares?: { count: number }
}

export interface FBComment {
  id: string
  message: string
  created_time: string
  from?: { id: string; name: string }
}

export function usePageEngagement(pageId: string | undefined) {
  return useQuery<{ data: PagePost[] }>({
    queryKey: ['page-engagement', pageId],
    queryFn: () => v1get(`/pages/${pageId}/engagement?limit=20`),
    enabled: !!pageId,
  })
}

// Edit published FB post message
export function useEditFBPost() {
  return useMutation({
    mutationFn: ({ pageId, postId, message }: { pageId: string; postId: string; message: string }) =>
      v1put(`/pages/${pageId}/posts/${postId}`, { message }),
  })
}

// Delete published FB post
export function useDeleteFBPost() {
  return useMutation({
    mutationFn: ({ pageId, postId }: { pageId: string; postId: string }) =>
      v1del(`/pages/${pageId}/posts/${postId}`),
  })
}

// Get comments on FB post
export function useFBPostComments(pageId: string | undefined, postId: string | undefined) {
  return useQuery<{ data: FBComment[] }>({
    queryKey: ['fb-comments', pageId, postId],
    queryFn: () => v1get(`/pages/${pageId}/posts/${postId}/comments`),
    enabled: !!pageId && !!postId,
  })
}

// Add comment to FB post
export function useAddFBComment() {
  return useMutation({
    mutationFn: ({ pageId, postId, message }: { pageId: string; postId: string; message: string }) =>
      v1post(`/pages/${pageId}/posts/${postId}/comments`, { message }),
  })
}

// Service status health check
export interface ServiceStatus {
  name: string
  status: 'operational' | 'degraded' | 'maintenance' | 'offline'
  responseTime: number
  details?: string
}

export interface HealthCheckResponse {
  timestamp: string
  services: ServiceStatus[]
}

export function useServiceStatus() {
  return useQuery<HealthCheckResponse>({
    queryKey: ['service-status'],
    queryFn: () => v1get('/status/health'),
    refetchInterval: 60000,
  })
}

// Delete comment on FB post
export function useDeleteFBComment() {
  return useMutation({
    mutationFn: ({ pageId, commentId }: { pageId: string; commentId: string }) =>
      v1del(`/pages/${pageId}/comments/${commentId}`),
  })
}
